import type {
  CustomerPurchasedSubscriptionDetail,
  CustomerSubscriptionRecentBilling,
} from "./types";
import {resolveStoreSubscriptionIdForReviews} from "../shared/resolve-store-subscription-id";

function normalizeRecentBillings(raw: unknown): CustomerSubscriptionRecentBilling[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const billings: CustomerSubscriptionRecentBilling[] = [];

  raw.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const o = item as Record<string, unknown>;
    const id = Number(o.id ?? 0);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    const amountRaw = o.amount;
    const amount =
      typeof amountRaw === "number"
        ? amountRaw
        : typeof amountRaw === "string"
        ? amountRaw
        : 0;

    billings.push({
      id,
      amount,
      currency: typeof o.currency === "string" ? o.currency : "USD",
      status: o.status as string | number | undefined,
      statusName: typeof o.statusName === "string" ? o.statusName : undefined,
      transactionType:
        typeof o.transactionType === "number" ? o.transactionType : undefined,
      createdAt:
        typeof o.createdAt === "string"
          ? o.createdAt
          : typeof o.created_at === "string"
          ? o.created_at
          : undefined,
    });
  });

  return billings;
}

export function normalizeCustomerPurchasedSubscriptionDetail(
  raw: unknown
): CustomerPurchasedSubscriptionDetail | null {
  const inner =
    raw && typeof raw === "object" && "data" in raw ? (raw as {data: unknown}).data : raw;
  if (!inner || typeof inner !== "object") {
    return null;
  }
  const o = inner as Record<string, unknown>;
  const coach =
    o.coach && typeof o.coach === "object" ? (o.coach as Record<string, unknown>) : null;
  const customerEnrollmentExplicit = Number(
    o.customerSubscriptionId ?? o.customer_subscription_id ?? 0
  );
  const enrollmentFallback = Number(o.id ?? 0);
  const enrollmentId =
    customerEnrollmentExplicit > 0 ? customerEnrollmentExplicit : enrollmentFallback;

  if (!Number.isFinite(enrollmentId) || enrollmentId <= 0) {
    return null;
  }

  const catalogResolved = resolveStoreSubscriptionIdForReviews(o, enrollmentId);
  const storeSubscriptionId = catalogResolved > 0 ? catalogResolved : enrollmentId;

  const subscriptionNameFromApi =
    typeof o.subscriptionName === "string" && o.subscriptionName.trim() !== ""
      ? o.subscriptionName.trim()
      : undefined;

  const amountRaw = o.amount;
  const amount =
    typeof amountRaw === "number"
      ? amountRaw
      : typeof amountRaw === "string"
      ? amountRaw
      : undefined;
  const amountCurrency = typeof o.currency === "string" ? o.currency : undefined;
  const tierName = typeof o.tierName === "string" ? o.tierName : undefined;
  const statusName = typeof o.statusName === "string" ? o.statusName : undefined;
  const billingInterval =
    typeof o.billingInterval === "number" ? o.billingInterval : undefined;
  const billingDuration =
    typeof o.billingDuration === "number" ? o.billingDuration : undefined;
  const billingCycles =
    o.billingCycles === null
      ? null
      : typeof o.billingCycles === "number"
      ? o.billingCycles
      : undefined;
  const startDate = typeof o.startDate === "string" ? o.startDate : undefined;
  const nextBillingDate =
    typeof o.nextBillingDate === "string" ? o.nextBillingDate : undefined;
  const totalSpend = typeof o.totalSpend === "number" ? o.totalSpend : undefined;

  const createdAtFromLegacy =
    typeof o.createdAt === "string"
      ? o.createdAt
      : typeof o.created_at === "string"
      ? o.created_at
      : undefined;

  return {
    id: enrollmentId,
    storeSubscriptionId,
    subscriptionName: subscriptionNameFromApi,
    name: String(o.subscriptionName ?? o.name ?? o.title ?? "Subscription"),
    tierName,
    amount,
    amountCurrency,
    statusName,
    billingInterval,
    billingDuration,
    billingCycles,
    startDate,
    nextBillingDate,
    totalSpend,
    slug: typeof o.slug === "string" ? o.slug : undefined,
    description: typeof o.description === "string" ? o.description : undefined,
    status: o.status as string | number | undefined,
    createdAt: createdAtFromLegacy ?? startDate,
    updatedAt:
      typeof o.updatedAt === "string"
        ? o.updatedAt
        : typeof o.updated_at === "string"
        ? o.updated_at
        : undefined,
    coverImage:
      typeof o.coverImage === "string"
        ? o.coverImage
        : typeof o.cover_image === "string"
        ? o.cover_image
        : undefined,
    coachName:
      typeof o.coachName === "string"
        ? o.coachName
        : typeof o.coach_name === "string"
        ? o.coach_name
        : coach
        ? String(coach.displayName ?? coach.name ?? "")
        : undefined,
    coachId:
      typeof o.coachId === "number"
        ? o.coachId
        : typeof o.coach_id === "number"
        ? o.coach_id
        : coach && typeof coach.id === "number"
        ? coach.id
        : undefined,
    redirectUrl:
      o.redirectUrl === null
        ? null
        : typeof o.redirectUrl === "string"
        ? o.redirectUrl
        : typeof o.redirect_url === "string"
        ? o.redirect_url
        : undefined,
    tinyUrl:
      typeof o.tinyUrl === "string"
        ? o.tinyUrl
        : typeof o.tiny_url === "string"
        ? o.tiny_url
        : undefined,
    bookingQuestions: Array.isArray(o.bookingQuestions)
      ? o.bookingQuestions.map((q) =>
          typeof q === "string" ? q : String((q as {question?: unknown}).question ?? "")
        )
      : Array.isArray(o.booking_questions)
      ? o.booking_questions.map((q) =>
          typeof q === "string" ? q : String((q as {question?: unknown}).question ?? "")
        )
      : undefined,
    recentBillings: normalizeRecentBillings(o.recentBillings ?? o.recent_billings),
  };
}
